/**
 * @module services/zotero/helpers
 * @description Utility functions for Zotero API interactions
 */

// =============================================================================
// QUERY STRING
// =============================================================================

/**
 * Build query string from params object
 */
export function buildQueryString(
  params: Record<string, string | number | boolean | undefined>
): string {
  const entries = Object.entries(params)
    .filter(([, value]) => value !== undefined)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);

  return entries.length > 0 ? `?${entries.join('&')}` : '';
}

// =============================================================================
// LINK HEADER PARSING
// =============================================================================

/**
 * Parse Link header for pagination
 * @example
 * parseLinkHeader('<https://api.zotero.org/...?start=25>; rel="next"')
 * // Returns: { next: 'https://api.zotero.org/...?start=25' }
 */
export function parseLinkHeader(linkHeader: string | null): Record<string, string> {
  if (!linkHeader) return {};

  const links: Record<string, string> = {};
  const parts = linkHeader.split(',');

  for (const part of parts) {
    const match = part.match(/<([^>]+)>;\s*rel="([^"]+)"/);
    if (match) {
      links[match[2]] = match[1];
    }
  }

  return links;
}

// =============================================================================
// ERROR HANDLING
// =============================================================================

/**
 * Extract error message from HTTP response
 */
export async function extractErrorMessage(response: Response): Promise<string> {
  try {
    const text = await response.text();
    try {
      const json = JSON.parse(text);
      return json.message || json.error || text;
    } catch {
      return text || response.statusText;
    }
  } catch {
    return response.statusText;
  }
}
